import { BedrockRuntimeClient, InvokeModelCommand, ApplyGuardrailCommand } from '@aws-sdk/client-bedrock-runtime';
import { CognitoService } from 'src/services/aws/CognitoService';
import { SSMService } from 'src/services/aws/SSMService';
import { logger } from 'src/logger';

const BEDROCK_REGION = 'us-west-2';
const MODEL_ID = 'us.anthropic.claude-sonnet-4-5-20250929-v1:0';
const GUARDRAIL_ID_PARAM = '/das-finsuite/guardrail/EAHuddleGuardrail/id';
const GUARDRAIL_VERSION_PARAM = '/das-finsuite/guardrail/EAHuddleGuardrail/version';

export class BedrockService {
  private static client: BedrockRuntimeClient | null = null;
  private static guardrailId: string | null = null;
  private static guardrailVersion: string | null = null;

  private static getClient(): BedrockRuntimeClient {
    if (this.client) return this.client;
    this.client = new BedrockRuntimeClient({
      region: BEDROCK_REGION,
      credentials: CognitoService.credentialProvider
    });
    return this.client;
  }

  private static async loadGuardrailConfig(): Promise<{ guardrailId: string; guardrailVersion: string }> {
    if (this.guardrailId && this.guardrailVersion) {
      return { guardrailId: this.guardrailId, guardrailVersion: this.guardrailVersion };
    }
    const [id, version] = await Promise.all([
      SSMService.retrieveParameter(GUARDRAIL_ID_PARAM),
      SSMService.retrieveParameter(GUARDRAIL_VERSION_PARAM)
    ]);
    this.guardrailId = id;
    this.guardrailVersion = version;
    return { guardrailId: id, guardrailVersion: version };
  }

  /**
   * Apply guardrail check on content separately from model invocation.
   * This prevents false positives from PROMPT_ATTACK filters on directive prompt language.
   */
  private static async applyGuardrail(source: 'INPUT' | 'OUTPUT', textContent: string): Promise<{ allowed: boolean; message?: string }> {
    const { guardrailId, guardrailVersion } = await this.loadGuardrailConfig();
    if (!guardrailId) return { allowed: true };

    try {
      const client = this.getClient();
      const response = await client.send(
        new ApplyGuardrailCommand({
          guardrailIdentifier: guardrailId,
          guardrailVersion,
          source,
          content: [{ text: { text: textContent } }]
        })
      );

      if (response.action === 'GUARDRAIL_INTERVENED') {
        const blockedMessage = response.outputs?.[0]?.text || 'Content blocked by guardrail policy';
        logger.error('Guardrail intervened', { source, assessments: response.assessments });
        return { allowed: false, message: blockedMessage };
      }
      return { allowed: true };
    } catch (error) {
      logger.error('Error calling ApplyGuardrail API', { error });
      // Fail open — model invocation still has standard Bedrock safety checks
      return { allowed: true };
    }
  }

  /**
   * Invoke the Bedrock foundation model using invoke_model (Anthropic Messages API).
   * Guardrail is applied separately on input/output to avoid false positives.
   */
  public static async invokeWithText(userMessage: string, systemPrompt?: string): Promise<string> {
    const client = this.getClient();

    // Step 1: Apply guardrail on user input only
    const inputCheck = await this.applyGuardrail('INPUT', userMessage);
    if (!inputCheck.allowed) {
      throw new Error(inputCheck.message);
    }

    // Step 2: Invoke model without guardrail params
    const requestBody = {
      anthropic_version: 'bedrock-2023-05-31',
      max_tokens: 4096,
      ...(systemPrompt && { system: systemPrompt }),
      messages: [{ role: 'user', content: [{ type: 'text', text: userMessage }] }],
      temperature: 0.2
    };

    logger.info('BedrockService: Invoking model', { modelId: MODEL_ID });

    const response = await client.send(
      new InvokeModelCommand({
        modelId: MODEL_ID,
        contentType: 'application/json',
        accept: 'application/json',
        body: JSON.stringify(requestBody)
      })
    );

    const responseBody = JSON.parse(new TextDecoder().decode(response.body));
    const responseText: string = responseBody.content?.[0]?.text || '';

    // Step 3: Apply guardrail on model output
    const outputCheck = await this.applyGuardrail('OUTPUT', responseText);
    if (!outputCheck.allowed) {
      throw new Error(outputCheck.message);
    }

    return responseText;
  }
}
